import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import type { Educando } from 'shared';
import { isCertificateTemplate } from 'shared';
import BadgesList from '@/components/BadgesList';
import PercursoSummary from '@/components/PercursoSummary';
import PercursoTrail from '@/components/PercursoTrail';
import PresenceBadge from '@/components/PresenceBadge';
import { useEducandoBadges, useEducandoPercurso, useEducandos } from '@/hooks/useGuardian';
import { ageOn, educandoName } from '@/lib/educando';

type Tab = 'percurso' | 'badges';

/** One educando as their guardian sees them: presence, percurso and badges. */
export default function EducandoPage() {
  const participantId = Number(useParams().id);
  const educandosQuery = useEducandos();
  const educando = educandosQuery.data?.educandos.find((e) => e.id === participantId);

  return (
    <>
      <Helmet>
        <title>{educando ? `${educandoName(educando)} | AICD` : 'Educando | AICD'}</title>
      </Helmet>
      <div className="space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Educandos
        </Link>

        {educandosQuery.isLoading && <div className="h-24 animate-pulse rounded-2xl bg-input-soft" aria-hidden="true" />}

        {educandosQuery.isError && (
          <div role="alert" className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy">
            Não foi possível carregar este educando. Tente novamente mais tarde.
          </div>
        )}

        {educandosQuery.isSuccess && !educando && (
          <div role="alert" className="rounded-2xl border border-dashed border-border-strong bg-card p-6 text-sm text-muted-foreground">
            Este educando não está associado à sua conta.
          </div>
        )}

        {educando && <EducandoDetail educando={educando} />}
      </div>
    </>
  );
}

function EducandoDetail({ educando }: { educando: Educando }) {
  const [tab, setTab] = useState<Tab>('percurso');
  const name = educandoName(educando);

  return (
    <>
      <header className="space-y-2">
        <h1 className="text-2xl font-black text-foreground">{name}</h1>
        <p className="text-sm text-muted-foreground">
          {ageOn(educando.dataNascimento)} anos
          {educando.digitalCenterName && ` · ${educando.digitalCenterName}`}
        </p>
        <PresenceBadge educando={educando} />
      </header>

      <div role="tablist" aria-label="Secções do educando" className="grid grid-cols-2 gap-1 rounded-2xl bg-input-soft p-1">
        {(['percurso', 'badges'] as const).map((t) => (
          <button
            key={t}
            id={`tab-${t}`}
            type="button"
            role="tab"
            aria-selected={tab === t}
            aria-controls={`panel-${t}`}
            onClick={() => setTab(t)}
            className={`rounded-xl py-2 text-sm font-bold transition ${
              tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            }`}
          >
            {t === 'percurso' ? 'Percurso' : 'Badges'}
          </button>
        ))}
      </div>

      <div id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {tab === 'percurso' ? (
          <EducandoPercurso participantId={educando.id} />
        ) : (
          <EducandoBadges educando={educando} name={name} />
        )}
      </div>
    </>
  );
}

function EducandoPercurso({ participantId }: { participantId: number }) {
  const query = useEducandoPercurso(participantId);
  if (query.isLoading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-input-soft" aria-hidden="true" />;
  }
  if (query.isError || !query.data) {
    return (
      <div role="alert" className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy">
        Não foi possível carregar o percurso. Tente novamente mais tarde.
      </div>
    );
  }
  const stops = query.data.stops ?? [];
  return (
    <div className="space-y-6">
      {stops.length > 0 && <PercursoSummary totals={query.data.totals} />}
      <PercursoTrail stops={stops} forGuardian />
    </div>
  );
}

function EducandoBadges({ educando, name }: { educando: Educando; name: string }) {
  const query = useEducandoBadges(educando.id);
  const template = isCertificateTemplate(educando.certificateTemplate) ? educando.certificateTemplate : null;
  return (
    <BadgesList
      isLoading={query.isLoading}
      isError={query.isError}
      badges={query.data?.badges ?? []}
      participantName={name}
      participantReady
      certificateTemplate={template}
      centerName={educando.digitalCenterName}
      forGuardian
    />
  );
}
